import {Inject, Injectable, NgZone} from '@angular/core';
import { HttpInterceptorService } from '../../../core/services/http-interceptor.service';
import {  AberrationsActionCreatorService } from './aberrations-action-creator.service';
import { Subscription } from 'rxjs/Subscription';
import { IAberrations } from '../interfaces/aberrations.interface';
import { Observable } from 'rxjs/Observable';
import { ArrayUtils } from '../../../shared/utils/Array.utils';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { ConnectableObservable } from 'rxjs/Rx';
import { AberrationsMetaActionCreatorService } from './aberrations-meta-action-creator.service';
import { InterpolateService } from '../../../shared/services/interpolate.service';
import { StoreActionCreatorService } from '../../../shared/services/store-action-creator.service';
import {
    IAnnotationSearchableLink,
    IModalitySearch,
    ISearchSource,
    SearchSource
} from '../interfaces/annotation-searchable-links.interface';
import {getActiveRecordSync} from "../../../workflow-config/utils/store.utils";
import {ResourceRestService} from "../../../workflow-config/services/resource-rest.service";
import {Resources} from "../../../workflow-config/constants/resources.enum";
import {WorkflowConfigService} from "../../../workflow-config/services/workflow-config.service";
import {filter, map, take, bufferTime, switchMap, share, flatMap, publish, concatAll} from 'rxjs/operators';
import {FilterService} from '../../../filter/services/filter.service';
import { of, empty } from 'rxjs';
import { isEmpty } from 'lodash';
import {Updated} from "@ispm/test/components/editable-annotation/editable-annotations.component";
import { Actions, ofType } from '@ngrx/effects';
import { TestService } from '@ispm/dashboard/test/services/test.service';
import { TestState, InitTestState } from '@ispm/dashboard/test/store/test.actions';

@Injectable()
export class AberrationsService {
    static readonly URL = '/aberration/';
    static readonly SEARCH_LINK_URL = '/searchlinks/';
    static readonly IDS_CHUNK_AMOUNT = 10000;
    static readonly CHUNK_AMOUNT = 350;
    static readonly MAX_QUERIES_AMOUNT = 1000000;
    static readonly MAX_THREADS = 4;
    static readonly LOADING_MSG = 'Calculating...';

    public init: any = { };
    public filterMessage: BehaviorSubject<[string]> = new BehaviorSubject<[string]>([AberrationsService.LOADING_MSG]);
    public dataRequester$: ConnectableObservable<{ results: IAberrations[], first: boolean }>;

    private chunkSize: number;
    private displayColumns: Map<string, any> = new Map<string, any>();
    private requests: Map<string, { query: string, request: any, subscriber: Subscription }>
        = new Map<string, { query: string, request: any, subscriber: Subscription }>();
    private threadsAmount = 0;
    private threads = [];
    private searchTerm: string;
    private opinionSaveDispatcher: Subject<any> = new Subject();
    private opinionSavePipe: ConnectableObservable<any> = null;
    public aberrationUpdateSubject: Subject<{ id: string, updated: Updated }> = new Subject();
    private dataRequester: Subject<{
        workflowRequestId: string,
        aberrationType: string,
        aberrationClassType: string,
        searchTerm: string,
        testTypeId: string,
        revisionId: string,
        start: number,
        offset: number,
        requestedRevisionId: string,
        sortBy: { dir: string, column: string, sortAsExpression: string }
    } | null> = new Subject<{
        workflowRequestId: string,
        aberrationType: string,
        aberrationClassType: string,
        searchTerm: string,
        testTypeId: string,
        revisionId: string,
        start: number,
        offset: number,
        requestedRevisionId: string,
        sortBy: { dir: string, column: string, sortAsExpression: string }
    } | null>();
    private maxAmount: number;

  /**
   * Query the list of ids, sorted and filtered
   * Using paging recursively
   *
   * @param {string} workflowRequestId
   * @param {string} revisionId
   * @param {string} aberrationType
   * @param {string} aberrationClassType
   * @param {Ifilter} filterQueryObject
   * @param {string} testTypeId
   * @param {boolean} toggleLoader
   * @param {number} page
   * @param {number} amountToQuery
   * @param sortBy use for sorting date
   * @param requestedRevisionId use for getting the manual curation status
   * @return {Observable<{ ids: string[], page: number }> }
   */


  objToStr(obj) {
    let str = '';
    for (let p in obj) {
      if (obj.hasOwnProperty(p)) {
        str += '&' + p + '=' + obj[p];
      }
    }
    return str;
  }

  public getAberrationsListByType(workflowRequestId: string,
                                  revisionId: string,
                                  aberrationType: string,
                                  aberrationClassType: string,
                                  filterQueryObject: any,
                                  testTypeId: string,
                                  toggleLoader: boolean = true,
                                  page: number = 0,
                                  amountToQuery: number = this.getIdsChunkAmount(),
                                  requestedRevisionId?: string,
                                  sortBy?: { column: string, dir: string, sortAsExpression: string },

  ): Observable<{ ids: string[], page: number }> {

        let last = false;
        let filterQuery;
        if (filterQueryObject && Object.keys(filterQueryObject).includes("selectedFilterValues")) {
            filterQuery = this.filterService.buildFilterString(filterQueryObject.selectedFilterValues);
        } else {
            filterQuery = this.filterService.buildFilterString(filterQueryObject);
        }

        // Important: need to check if sortBy is an object with at least 1 key
        const sortFilter = !isEmpty(sortBy) ? this.objToStr(sortBy) : this.objToStr({ column: undefined, dir: undefined, sortAsExpression: undefined });
        const cacheFilterQuery = `${filterQuery}${sortFilter}`;

        let request = this.getCachedRequest(aberrationClassType, cacheFilterQuery, page);
        if (!!request) {
            return request;
        }

        const key = AberrationsService.buildCacheKey(aberrationClassType, page);
        request = this.checkCachedData(aberrationClassType, cacheFilterQuery)
            .flatMap((cached: boolean) => {
                if (cached && page === 0) {
                    return Observable.throw({ message: 'cached', code: 0});
                } else if (toggleLoader && page === 0) {
                    this.init[aberrationClassType] = false;
                }
                if (page === 0) {
                    this.aberrationAC.setLoadingStatus(aberrationClassType, true);
                    this.aberrationAC.setFilters(aberrationClassType, cacheFilterQuery, filterQueryObject);
                }

        return Observable.of([]);
      })
      .flatMap(() => {
        let listkey = 'list_'+key;
        let req = this.getCachedRequest(listkey,cacheFilterQuery, page);
        if(!req ) {
          req = this.resourceRest.post(AberrationsService.URL + 'getAberrations/' + aberrationClassType,
            {
              context: ResourceRestService.getResourcesString(Resources.Aberration, Resources.FirstRevision),
              filterQuery: filterQuery,
              sortBy,
              aberrationType: aberrationClassType,
              aberrationKey: aberrationType,
              testTypeId: testTypeId,
              page: page,
              amountToQuery: amountToQuery,
              fetchCount: page === 0
            })
            .map(res => res.json())
            .share()
            .finally(() => {
              this.uncacheOngoingRequest(listkey);
            })
            .publishReplay(1);

          this.cacheOngoingRequest(listkey, req, req.connect(), cacheFilterQuery, aberrationType);
        }
        return req;
      })
      .takeUntil(this.workflowConfig.listenToWorkflowChange())
      // .takeUntil(this.dataRequester.asObservable().pipe(filter (requester => requester.aberrationClassType === aberrationClassType )))
      .map(({ids, count}) => {
        if (page === 0) {
          this.aberrationAC.setFilteredList(aberrationClassType, ids, count);
        } else {
          this.aberrationAC.appendFilteredList(aberrationClassType, ids);
        }

        return ids;
      })
      .do((idsArray) => {
        if (idsArray.length === this.getIdsChunkAmount()) {
          this.getAberrationsListByType(
            workflowRequestId,
            revisionId,
            aberrationType,
            aberrationClassType,
            filterQueryObject,
            testTypeId,
            toggleLoader,
            ++page,
            this.getIdsChunkAmount(),
            requestedRevisionId,
            sortBy,
            ).subscribe();
        } else {
          last = true;
        }

      })
      .catch(err => {
        if (err.message === 'cached') {
          return this.aberrationAC.getState(['filteredResults'])
            .pipe(
              filter((state: Map<string, any>) => state.has(aberrationClassType)),
              take(1),
              map((state: Map<string, any>) => state.get(aberrationClassType))
            );
        }
        else {
          return Observable.throw(err);
        }
      })
      .flatMap(data => {
        if (page === 0) {
          if (this.searchTerm) {
            this.dataRequestEmit(
              workflowRequestId,
              aberrationType,
              aberrationClassType,
              this.searchTerm,
              testTypeId,
              revisionId,
              0,
              0,
              requestedRevisionId,
              sortBy);
          } else {
            this.getAberrationData(workflowRequestId, aberrationType, aberrationClassType, testTypeId, revisionId, this.searchTerm, 0,
              AberrationsService.CHUNK_AMOUNT, data.slice(0, AberrationsService.CHUNK_AMOUNT), requestedRevisionId, sortBy)
              .finally(() => {
                this.init[aberrationType] = true;
              })
              .publish()
              .connect();
          }
        }
        return Observable.of({ ids: data, page, last});
      })
      .finally(() => {
        this.init[aberrationClassType] = true;
        this.requests.delete(key);
      })
      .share()
      .publishReplay(1);

    this.cacheOngoingRequest(key, request, request.connect(), cacheFilterQuery, aberrationType+'_pipe_'+page);
    return request;
  }

    private idsChunkAmount: number;

    constructor(private _http: HttpInterceptorService,
                private resourceRest: ResourceRestService,
                private testService : TestService,
                private aberrationAC: AberrationsActionCreatorService,
                private workflowConfig: WorkflowConfigService,
                private aberrationMetaAC: AberrationsMetaActionCreatorService,
                private interpolateService: InterpolateService,
                private storeService: StoreActionCreatorService,
                private filterService: FilterService,
                private _ngZone: NgZone,
                private actions$: Actions) {
        this.subscribeToAberrationActions(actions$);
        let first;
        let aberrationType, aberrationClassType;
         this._ngZone.runOutsideAngular(() => {
           this.opinionSavePipe = this.opinionSaveDispatcher.asObservable().pipe(
            bufferTime(3000),
            share(),
            map((selections: [{ aberrationId: string, selection: boolean, opinionId: string, revisionId: string, molecularFinding: any }]) => {
              if (!Array.isArray(selections) || selections.length < 1) {
                return empty();
              }
              let aberrationSelections: { id: string, selection: boolean }[] = selections.map(selection => {
                return {
                  id: selection.aberrationId,
                  selection: selection.selection
                }
              });
              let molecularFinding: any = selections[0].molecularFinding;
              let opinionId: string = selections[0].opinionId;
              let revisionId: string = selections[0].revisionId;
              return this._http.put(`${AberrationsService.URL}updateSelectionBulk/${opinionId}`, {
                selections: aberrationSelections,
                revision: revisionId,
                molecularFinding: molecularFinding
              }).pipe(map(r => r.body));
            }),
            concatAll()
          ).publish();
          this.opinionSavePipe.connect();
        });

        this.dataRequester$ = this.dataRequester.asObservable()
            .switchMap(parameters => {
                aberrationType = parameters.aberrationType;
                aberrationClassType = parameters.aberrationClassType;
                if (!!parameters.searchTerm) {
                    first = true;
                    this.searchTerm = parameters.searchTerm;
                    return this.filterBySearchTerm(parameters.workflowRequestId, parameters.aberrationType, parameters.aberrationClassType,
                        this.searchTerm, parameters.revisionId, parameters.testTypeId);
                } else {
                    this.searchTerm = parameters.searchTerm;
                    first = !parameters.start;
                    return this.getAberrationData(
                      parameters.workflowRequestId,
                      parameters.aberrationType,
                      parameters.aberrationClassType,
                      parameters.testTypeId,
                      parameters.revisionId,
                      this.searchTerm,
                      parameters.start,
                      parameters.offset,
                      null,
                      parameters.requestedRevisionId,
                      parameters.sortBy);
                }
            })
            .map(data => {
                const results = { results: data, first: first};
                first = false;
                this.aberrationAC.setLoadingStatus(aberrationClassType, false);
                return results;
            })
            .publish();
        this.dataRequester$.connect();
    }

    /**
     *
     * @param keys
     * @return {string}
     */
    public static buildCacheKey(...keys) {
        return keys.join('-');
    }

    public getAberrationsIdsByFilter(revisionId: string, testTypeId: string, aberrationType: string, aberrationClassType: string, filterQuery: string, sortBy) {
      return this.resourceRest.post(AberrationsService.URL + 'getAberrations/' + aberrationClassType, {
          context:        revisionId,
          filterQuery:    filterQuery,
          aberrationType: aberrationClassType,
          aberrationKey:  aberrationType,
          testTypeId:     testTypeId,
          page:           0,
          amountToQuery:  this.getIdsChunkAmount(),
          sortBy
        })
        .pipe(map(res => res.body));
    }


    /**
     *  fetching data for aberration passed by ids array
     *
     * @param {string} workflowRequestId
     * @param {string} aberrationType
     * @param {string} aberrationClassType
     * @param {string} testTypeId
     * @param {string} revisionId
     * @param {string} searchTerm
     * @param {number} start
     * @param {number} offset
     * @param {string[]} ids
     *
     * @param requestedRevisionId use to get manual curation status
     * @param sortBy
     * @return {Promise<IAberrations[]>}
     */
    public getAberrationData(workflowRequestId: string,
                             aberrationType: string,
                             aberrationClassType: string,
                             testTypeId: string,
                             revisionId: string,
                             searchTerm: string = '',
                             start: number = 0, offset: number = 0,
                             ids: string[] = null,
                             requestedRevisionId?: string,
                             sortBy?: { dir: string, column: string, sortAsExpression: string }): Observable<IAberrations[]> {

        if(!(aberrationType && aberrationClassType)){
            return Observable.of([]);
        }
        this.searchTerm = searchTerm;
        let key;
        let filtered;
        const resolveData = [];
        let chunkSize = this.getChunkSize();
        const NOOP = 'NOOP_' + start;
        if (chunkSize + offset >= this.getMaxAmount()) {
            return Observable.of([]);
        }
        return this.aberrationAC.getState()
            .take(1)
            .map(data => {
                if (ids) {
                    filtered = ids;
                } else {
                    const filteredResults = getActiveRecordSync(data['filteredResults']) || new Map();
                    filtered = filteredResults.has(aberrationClassType)
                        ? filteredResults.get(aberrationClassType)
                        : [];
                }
                const results = getActiveRecordSync(data['results']) || new Map();
                return {
                    filtered:     filtered,
                    wholeResults: results.has(aberrationClassType)
                                      ? results.get(aberrationClassType)
                                      : new Map()
                };
            })
            .map(status => {
                const missingDataArray = [];
                chunkSize += offset;
                for (let i = start; i < chunkSize && i < filtered.length; i++) {
                    if (!status.wholeResults.has(filtered[i])) {
                        missingDataArray.push(filtered[i]);
                        resolveData.push(NOOP);
                    } else {
                        resolveData.push(status.wholeResults.get(filtered[i]));
                    }
                }
                return missingDataArray;
            })
            .flatMap(missingIds => {
                key = AberrationsService.buildCacheKey(aberrationClassType, 'data', missingIds[0], missingIds[Math.floor(missingIds.length / 2)],
                    missingIds.length);
                const cached = this.getCachedRequest(key, searchTerm);
                if (!!(cached)) {
                    return cached;
                } else if (missingIds.length > 0) {
                    const request = this.getAberrationsById(
                        workflowRequestId,
                        missingIds,
                        revisionId,
                        testTypeId,
                        aberrationType,
                        aberrationClassType,
                        searchTerm,
                        requestedRevisionId,
                        sortBy)
                        .map((aberrations: IAberrations[]) => {
                            ArrayUtils.reservationMerge(resolveData, aberrations, NOOP);
                            return resolveData;
                        })
                        .catch(err => [])
                        .finally(() => {
                            this.requests.delete(key);
                        })
                        .publish();
                    this.cacheOngoingRequest(key, request, request.connect(), searchTerm, aberrationType);
                    return request;
                } else {
                    return Observable.of(resolveData);
                }
            });


    }

    /**
     * query aberrations by chunk of aberration ids
     *
     * @param {string} workflowRequestId
     * @param {string[]} ids
     * @param {string} revisionId
     * @param {string} testTypeId
     * @param {string} aberrationType
     * @param {string} aberrationClassType
     * @param {string} searchTerm

     * @param requestedRevisionId
     * @param sortBy
     */
    public getAberrationsById(workflowRequestId: string,
                              ids: string[],
                              revisionId: string,
                              testTypeId: string,
                              aberrationType: string,
                              aberrationClassType: string,
                              searchTerm?: string,
                              requestedRevisionId?: string,
                              sortBy?: { dir: string, column: string, sortAsExpression: string }) {
        if (ids.length < 1) {
            return Observable.empty();
        }
        const request = this.resourceRest.post(AberrationsService.URL + 'getAberrationsById', {
            workflowRequestId: ResourceRestService.getResourcesString(Resources.WorkflowRequest),
            ids,
            revisionId: ResourceRestService.getResourcesString(Resources.Aberration, Resources.FirstRevision),
            testTypeId,
            aberrationType,
            searchTerm,
            requestedRevisionId,
            sortBy
        })
            .takeUntil(this.workflowConfig.listenToWorkflowChange())
            .map(data => data.json())
            .do(aberrations => this.aberrationAC.setResolveAberrations(aberrationClassType, aberrations))
            .share();

        return request;
    }

    public saveBulkSelectionDelayed(aberrationId: string, opinionId: string, selection: boolean, revision: string, molecularFinding?:any) {
        this.opinionSaveDispatcher.next({aberrationId, opinionId, selection, revision, molecularFinding});
        return this.opinionSavePipe.pipe(take(1));
    }

    public saveSelection(aberrationId, opinionId, selection, revision) {
        return this._http.put(AberrationsService.URL + 'updateSelection/' + opinionId, {
            aberrationId: aberrationId,
            selection:    selection,
            revision:     revision
        });
    }

    /**
     * returned filtered aberrations results
     * @param {string} workflowRequestId
     * @param {string}aberrationsType
     * @param {string} aberrationClassType
     * @param {string} searchTerm
     * @param {IAberrations[]} results
     * @param {string} revisionId
     * @param {string} testTypeId
     * @return {IAberrations[]}
     */
    public filterBySearchTerm(workflowRequestId: string, aberrationsType: string, aberrationClassType: string, searchTerm: string,
                              revisionId: string, testTypeId: string): Observable<IAberrations[]> {
        this.aberrationAC.setLoadingStatus(aberrationClassType, true);
        const displayColumns = this.displayColumns.get(aberrationsType);
        const buffer = this.getChunkSize();
        let resultAmount = 0;
        let counter = 0;
        this.aberrationAC.setLoadingStatus(aberrationClassType, true);
        searchTerm = searchTerm.toLowerCase();
        const suffix = 'results queried';
        return this.aberrationAC.getState(['filteredResults'])
            .filter(state => state.has(aberrationClassType))
            .take(1)
            .do(() => this.setResultMessage(aberrationClassType, true, counter, true, suffix))
            .flatMap(state => {
                const data = state.get(aberrationClassType);
                return data && data instanceof Array && data.length > 0 ? data : Observable.throw({ code: -1});
            })
            .bufferCount(buffer)
            .concatMap((ids) => {
                return this.getAberrationData(workflowRequestId,
                    aberrationsType,
                    aberrationClassType,
                    testTypeId,
                    revisionId,
                    searchTerm,
                    0,
                    0,
                    ids
                ).retry(2);
            }, AberrationsService.MAX_THREADS)
            .takeWhile(term => this.searchTerm === searchTerm)
            .concatMap(filteredResults => {
                const promise = new Promise((resolve) => {
                    const worker = new Worker('/workers-transpiled/filterAberrations.js');
                    const results = [];
                    worker.onmessage = (msg) => {
                        const item = msg.data;
                        if (item !== '-1') {
                            results.push(filteredResults[item]);
                        } else {
                            worker.terminate();
                            resolve(results);
                        }
                    };
                    worker.postMessage({ dataToFilter: filteredResults, displayColumns, searchTerm});
                    this.threads.push(worker);
                });
                counter += filteredResults.length;
                return Observable.fromPromise(promise);
            }, AberrationsService.MAX_THREADS)
            .catch(err => {
                switch (err && err.code) {
                    case -1:
                        return Observable.of([]);
                    default:
                        return Observable.throw(err);
                }
            })
            .map((filteredResults) => {
                resultAmount += filteredResults.length;
                if(resultAmount > 0){
                    this.aberrationAC.setLoadingStatus(aberrationClassType, false);
                }
                this.setResultMessage(aberrationClassType, true, counter, true, suffix);
                return filteredResults;
            })
            .finally(() => {
                this.threads.forEach(thread => thread.terminate());
                this.threads.length = 0;
                this.aberrationAC.setLoadingStatus(aberrationClassType, false);
                this.setResultMessage(aberrationClassType, true, resultAmount);
            });
    }


    public setMaxAmount(amount: number = AberrationsService.MAX_QUERIES_AMOUNT) {
        this.maxAmount = amount || AberrationsService.MAX_QUERIES_AMOUNT;
    }

    public getMaxAmount(): number {
        return this.maxAmount || AberrationsService.MAX_QUERIES_AMOUNT;
    }

    public getChunkSize(): number {
        return this.chunkSize || AberrationsService.CHUNK_AMOUNT;
    }

    public getIdsChunkAmount(){
      return this.idsChunkAmount || AberrationsService.IDS_CHUNK_AMOUNT;
    }

    public setIdsChunkAmount(amount: number){
      this.idsChunkAmount = amount;
    }

    /**
     *
     * @param {number} chunkSize
     */
    public setChunkSize(chunkSize: number = AberrationsService.CHUNK_AMOUNT): void {
        this.chunkSize = chunkSize;
    }

    public setDisplayColumns(aberrationType: string, displayColumns: any): void {
        this.displayColumns.set(aberrationType, displayColumns);
    }

    public initState(): void {
        this.requests.forEach(cached => cached.subscriber.unsubscribe());
        this.requests = new Map();
        this.init = { };
        this.aberrationAC.initState();
        this.testService.modalitiesData['modalitiesMap'] = null;
        this.filterMessage.next([AberrationsService.LOADING_MSG]);
    }

    /**
     * get cached existing query
     * @param {string} aberrationClassType
     * @param {string} filterQuery
     * @param {string | number} page
     */
    public getCachedRequest(aberrationClassType: string,
                            filterQuery: string = null,
                            page?: number): any | null {

        // build the cache key. if 'page' is null or undefined, the cache key will include only 'aberrationClassType'
        const key = AberrationsService.buildCacheKey(aberrationClassType, page);
        if (this.requests.has(key)) {
            const requestObject = this.requests.get(key);
            if (filterQuery !== null && requestObject.query === filterQuery ) {
                return requestObject.request;
            } else {
                requestObject.subscriber.unsubscribe();
                this.requests.delete(key);
                const dataKey = AberrationsService.buildCacheKey(aberrationClassType, 'data');
                if (this.requests.has(dataKey)) {
                    this.requests.get(dataKey).subscriber.unsubscribe();
                    this.requests.delete(dataKey);
                }
            }
        }
        return null;
    }

    public generateModalitySearchLink(modalityLink: IModalitySearch, args: any): Observable<string> {

        const observSources = [];

        modalityLink.sources.forEach(modalitySource => {
            if (modalitySource.all) {
                modalitySource.all.forEach(all => {
                    if (all.field) {
                        observSources.push(this.extractFieldFromSource(all, args).take(1));
                    }
                });
            }
        });

    return Observable.forkJoin(observSources)
      .map((values: { fieldFromSource: any, searchSource: ISearchSource }[]) => {
        const objForInterpolation = { };
        values.forEach(v => objForInterpolation[v.searchSource.key] = v.fieldFromSource);
        const interpolationResult = this.interpolateService.interpolate(modalityLink.searchTemplate, objForInterpolation, true,
          { useEmptyValues: false, usePartialPath: false });

        if (InterpolateService.hasInterpolationString(interpolationResult)) {
          return null;
        } else {
          return interpolationResult;
        }
      });
  }

    private extractFieldFromSource(searchSource: ISearchSource, args): Observable<any> {
        const sourceLocation = this.getSearchSources(<string> searchSource.source, args);
        const interpolationOptions = { };
        const interpolatedField = this.interpolateService.interpolate(searchSource.field, args, true,
            { useEmptyValues: false, usePartialPath: false});
        return this.storeService.getObsValue<IAberrations>(interpolatedField.split('.'), sourceLocation)
            .map(fieldFromSource => {
                return { fieldFromSource, searchSource};
            });
    }

    public getSearchLinksConfiguration(keys: Array<string>): Observable<Array<IAnnotationSearchableLink>> {

        return Observable.from(keys)
            .flatMap(key => this.aberrationMetaAC.getSearchableLinksConfig(key))
            .map(searchableConfig => {
                if (searchableConfig.data) {
                    return Observable.of(searchableConfig.data);
                }
                return this._http.get(AberrationsService.SEARCH_LINK_URL + 'getsearchlinks', { ids: searchableConfig.key})
                    .map(result => {
                        if (result.body) {
                            this.aberrationMetaAC.setSearchableLinksConfig(searchableConfig.key, result.body);
                        }
                        return result.body;
                    });
            })
            .combineAll()
            .map((configs: Array<Array<IAnnotationSearchableLink>>) => {
                let configsArr = [];
                configs.forEach(c => configsArr = configsArr.concat(c));
                return configsArr;
            });
    }


    /**
     *returning observable of message for filter status
     *
     * @return {Observable<[string]>}
     */
    public getResultMessage(): Observable<[string]> {
        return this.filterMessage.asObservable();
    }

    /**
     * emitting message for filtered title
     * @param {string} aberrationType
     * @param {boolean} isLoaded
     * @param {number} resultSize
     * @param {boolean} showFromAmount
     * @param {string} suffix
     */
    public setResultMessage(aberrationType: string,
                            isLoaded: boolean,
                            resultSize: number = null,
                            showFromAmount: boolean = false,
                            suffix: string = 'results'): void {

        this.aberrationAC
            .getState(['filters'])
            .filter(filters => filters.has(aberrationType))
            .take(1)
            .map(filters => filters.asImmutable().get(aberrationType).filterQueryObject)
            .map(filters => filters ? filters.selectedFilterValues : { })
            .flatMap((filters) => {
                let filterAmount = 0;
                if (filters) {
                    filterAmount = Object.keys(filters).length;
                }

                let obs;
                if (!isLoaded) {
                    return Observable.of(AberrationsService.LOADING_MSG);
                } else if (resultSize !== null) {
                    if (showFromAmount) {
                        obs = this.aberrationAC
                            .getState(['filteredResultsCount'])
                            .filter(filteredResultsCount => filteredResultsCount.has(aberrationType))
                            .take(1)
                            .map(filteredResultsCount => filteredResultsCount.get(aberrationType))
                            .map((size) => resultSize + ' / ' + size);
                    } else {
                        obs = Observable.of(resultSize);
                    }
                } else {
                    obs = this.aberrationAC
                        .getState(['filteredResultsCount'])
                        .filter(filteredResultsCount => filteredResultsCount.has(aberrationType))
                        .take(1)
                        .map(filteredResultsCount => {
                            return filteredResultsCount.get(aberrationType);
                        });
                }
                return obs.map(size => size + (resultSize !== null || filterAmount > 0 ? ' filtered  ' : '  ') + suffix);
            })
            .subscribe((message) => {
                message = [message];
                if (!isLoaded || (showFromAmount && resultSize)) {
                    message.push(null);
                }
                this.filterMessage.next(message);
            });
    }

    public dataRequestEmit(workflowRequestId: string,
                           aberrationType: string,
                           aberrationClassType: string,
                           searchTerm: string,
                           testTypeId: string,
                           revisionId: string,
                           start: number,
                           offset: number,
                           requestedRevisionId?: string,
                           sortBy?: { dir: string, column: string, sortAsExpression: string }) {
        this.dataRequester.next({
            workflowRequestId,
            aberrationType,
            aberrationClassType,
            searchTerm,
            testTypeId,
            revisionId,
            start,
            offset,
            requestedRevisionId: requestedRevisionId,
            sortBy
        });
    }

    /**
     *
     * @param aberrationType
     * @param filterQueryString
     */
    private checkCachedData(aberrationType, filterQueryString) {
        // return this.filterService.getSelectedFilterQueryString('aberration', aberrationType)
        //     .take(1)
        //     .map((selectedQuery) => {
        //         if (selectedQuery == filterQueryString)
        //             return true;
        //         else
        //             return false;
        //     })
        return this.aberrationAC.getState(['filters'])
            .take(1)
            .map(filter => filter.has(aberrationType) && filter.get(aberrationType).filterQueryString === filterQueryString);
    }

    private cacheOngoingRequest(key: string, request: any, subscriber: Subscription, query: string, filterKey: string) {
       // this.filterService.setCurrentSelectedQueryString('aberration', filterKey, query);
        this.requests.set(key, { request, subscriber, query});
    }

    private uncacheOngoingRequest(key: string) {
         const cachedQuery = this.requests.get(key);
         if (cachedQuery) {
             cachedQuery.subscriber.unsubscribe();
         }

         this.requests.delete(key);
     }

    private getSearchSources(source: string, args: { [key: string]: any }): Observable<any> {
        switch (source) {
            case SearchSource.STORE_AC_SERVICE:
                return this.aberrationAC.getState();
            case SearchSource.ARGS:
                return Observable.of(args);
        }
    }

  getAberrationsFilteredResultsCount(): Observable<any> {
    return this.aberrationAC.getState()
      .pipe(
        map(data => getActiveRecordSync(data['filteredResultsCount']) || new Map())
      );
  }

  public subscribeToAberrationActions(actions$) {
    actions$.pipe(ofType<InitTestState>(TestState.INIT)).subscribe(() => {
        this.initState();
    })
}

}
